import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import { filterAndSortMovies, type MovieFilters } from "@/utils/movie";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar from "@/components/feature/FilterBar";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

interface CollectionMeta {
  title: string;
  desc: string;
  icon: string;
  sort: string;
  keywords: string;
}

const collections: Record<string, CollectionMeta> = {
  hot: {
    title: "热门电影热榜",
    desc: "按影迷关注热度排出的实时榜单，看看大家最近都在追哪些片子。",
    icon: "ri-fire-line",
    sort: "heat",
    keywords: "热门电影,电影热榜,黄色大片,电影推荐",
  },
  top: {
    title: "高分好评精选",
    desc: "评分 8.0 以上的口碑之作，闭眼入也不容易踩雷的安心片单。",
    icon: "ri-star-line",
    sort: "rating",
    keywords: "高分电影,电影推荐,口碑电影,黄色大片",
  },
  new: {
    title: "最新上映合集",
    desc: "近期上映的新片合集，第一时间掌握院线动态与观影选择。",
    icon: "ri-calendar-line",
    sort: "new",
    keywords: "最新电影,新片上映,电影资讯,黄色大片",
  },
};

export default function ListPage() {
  const { category = "" } = useParams();
  const isNews = category === "news";
  const meta = collections[category];
  const [filters, setFilters] = useState<MovieFilters>({ sort: "heat" });
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setFilters({ sort: meta?.sort ?? "heat" });
    setVisible(PAGE_SIZE);
  }, [category, meta?.sort]);

  useSeo({
    title: meta ? `${meta.title} - 黄色大片` : isNews ? "电影资讯合集 - 黄色大片" : "电影合集 - 黄色大片",
    description: meta
      ? `${meta.desc}黄色大片为你整理最新电影资讯和推荐，快速找到值得一看的影片。`
      : "黄色大片电影资讯合集，掌握最新电影动态。",
    keywords: meta ? meta.keywords : "电影资讯,黄色大片,电影合集",
  });

  const result = useMemo(() => filterAndSortMovies(movies, filters), [filters]);
  const shown = result.slice(0, visible);

  if (!meta && !isNews) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-500" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-50">未找到该合集</h1>
        <p className="mt-2 text-sm text-foreground-400">这个合集可能暂未开放，去看看热门榜单吧。</p>
        <Link
          to="/list/hot"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
        >
          <i className="ri-fire-line text-lg" />
          查看热门榜单
        </Link>
      </div>
    );
  }

  const heading = isNews
    ? { title: "电影资讯合集", desc: "汇总近期电影圈的重要动态与话题报道。", icon: "ri-newspaper-line" }
    : { title: meta.title, desc: meta.desc, icon: meta.icon };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "电影合集" }, { label: heading.title }]} />

      <div className="mt-5 flex flex-col gap-4 border-b border-background-200/50 pb-6 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-400">
            <i className={`${heading.icon} text-2xl`} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">{heading.title}</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-400">{heading.desc}</p>
          </div>
        </div>
        <p className="text-sm text-foreground-500">
          共 <span className="font-semibold text-primary-400">{isNews ? news.length : result.length}</span> 条内容
        </p>
      </div>

      <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-1">
        {[
          { key: "hot", label: "热门热榜", icon: "ri-fire-line" },
          { key: "top", label: "高分精选", icon: "ri-star-line" },
          { key: "new", label: "最新上映", icon: "ri-calendar-line" },
          { key: "news", label: "资讯合集", icon: "ri-newspaper-line" },
        ].map((item) => (
          <Link
            key={item.key}
            to={`/list/${item.key}`}
            className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              item.key === category
                ? "bg-primary-500 text-foreground-950"
                : "bg-background-100 text-foreground-300 hover:bg-background-200/70 hover:text-foreground-50"
            }`}
          >
            <i className={`${item.icon} text-sm`} />
            {item.label}
          </Link>
        ))}
      </div>

      {isNews ? (
        <div className="mt-6 flex flex-col gap-3">
          {news.map((item) => (
            <Link
              key={item.id}
              to={`/detail/${item.movieId}`}
              className="group flex gap-4 rounded-lg border border-background-200/50 bg-background-100 p-3 transition-colors hover:border-primary-400/60 md:p-4"
            >
              <div className="h-20 w-28 flex-shrink-0 overflow-hidden rounded-md bg-background-200 md:h-24 md:w-40">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 黄色大片 电影资讯`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-center gap-2 text-xs">
                  <span className="rounded-sm bg-primary-100 px-1.5 py-0.5 font-label uppercase tracking-wide text-primary-400">
                    {item.tag}
                  </span>
                  <span className="text-foreground-500">{item.date}</span>
                </div>
                <h3 className="mt-2 line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-50 group-hover:text-primary-400 md:text-base">
                  {item.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 hidden text-xs leading-relaxed text-foreground-400 md:block">
                  {item.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <>
          <div className="mt-5">
            <FilterBar filters={filters} onChange={setFilters} showRegion showTag showKeyword />
          </div>

          {result.length === 0 ? (
            <div className="flex flex-col items-center py-24 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-500" />
              <h2 className="mt-5 font-heading text-xl font-bold text-foreground-50">暂无符合条件的电影</h2>
              <p className="mt-2 text-sm text-foreground-400">筛选条件太严格了，重置一下试试看。</p>
              <button
                type="button"
                onClick={() => setFilters({ sort: meta?.sort ?? "heat" })}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-refresh-line text-lg" />
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {shown.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              {visible < result.length && (
                <div className="mt-9 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((value) => value + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/60 px-7 py-3 font-label text-sm uppercase tracking-wide text-foreground-100 transition-colors hover:border-primary-400 hover:text-primary-400"
                  >
                    <i className="ri-add-line text-lg" />
                    加载更多（{result.length - visible}）
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}